import { isEqual, isUndefined, isNull } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, fork, put, select, takeLeading } from 'redux-saga/effects';

import { getAllClientDatasetsByParentId, getDatasetDefinitionsByParentId, upsertClientRiskTolerance } from '../../../../services/dataset';
import { DatasetDefinition, DocumentSpecificHiddenFields, HiddenFields } from '../../../datasets/store/types';
import { ClientDatasets } from '../../client-datasets/store/types';
import { MyRiskTolerance, RiskTolerance, UpdatedRiskField } from '../../risk-tolerance/store/types';
import { fetchAllRiskToleranceDatasetsFailed, fetchAllRiskToleranceDatasetsSuccessful, fetchRiskToleranceDatasetDefinitionsFailed, fetchRiskToleranceDatasetDefinitionsStarted, fetchRiskToleranceDatasetDefinitionsSuccessful, saveRiskToleranceFailed, saveRiskToleranceSuccessful, setClientRiskToleranceView, setRiskFieldsUpdated, setRiskToleranceUpdated, toggleRiskFieldsUpdatedModal } from './actions';
import { getCurrentRiskToleranceConfig, getClientRiskToleranceConfig, getSelectedDatasetId, getRiskFieldsUpdated, getRiskToleranceIncludesOverrides } from './selectors';
import { ClientRiskToleranceActionTypes, ClientRiskToleranceView } from './types';
import { stripEmptyTextRiskConfig } from '../../risk-tolerance/utils';

export function* attemptFetchAllClientRiskToleranceDatasets() {
    try {
        const datasetDocumentNamesAndAgreementTypes: ClientDatasets[] = yield call(getAllClientDatasetsByParentId);
        yield put(fetchAllRiskToleranceDatasetsSuccessful(datasetDocumentNamesAndAgreementTypes));
    } catch (e) {
        toast.error('Unable to fetch the dataset document names and types. Please try again.');
        yield put(fetchAllRiskToleranceDatasetsFailed((e as Error).message));
    }
}

function* fetchAllClientRiskToleranceDatasetsWatcher() {
    yield takeLeading(ClientRiskToleranceActionTypes.FETCH_ALL_CLIENT_RISK_TOLERANCE_DATASETS_STARTED, attemptFetchAllClientRiskToleranceDatasets);
}

export function* redirectClientRiskToleranceDatasetView({ payload }: ReturnType<typeof setClientRiskToleranceView>) {
    const { view, datasetId, redirect } = payload;
    if (view !== ClientRiskToleranceView.LIST && !isNull(datasetId)) {
        const navigationPath = `/admin/risk-tolerance/${datasetId}`;
        if (redirect) {
            yield put(push(navigationPath));
        }
    } else {
        const navigationPath = '/admin/risk-tolerance';
        yield put(push(navigationPath));
    }
}

function* redirectClientRiskToleranceDatasetViewWatcher() {
    yield takeLeading(ClientRiskToleranceActionTypes.CLIENT_RISK_TOLERANCE_VIEW, redirectClientRiskToleranceDatasetView);
}

export function* attemptFetchClientRiskToleranceDatasetDefinitions({ payload }: ReturnType<typeof fetchRiskToleranceDatasetDefinitionsStarted>) {
    try {
        const { definitions, hiddenFields, riskToleranceDefinitions, documentHiddenFields, riskToleranceIsRecalculating, riskToleranceIncludesOverrides }: { definitions: DatasetDefinition[]; hiddenFields: HiddenFields; riskToleranceDefinitions: (RiskTolerance | MyRiskTolerance)[]; documentHiddenFields: DocumentSpecificHiddenFields; riskToleranceIsRecalculating: boolean; riskToleranceIncludesOverrides: boolean; } = yield call(getDatasetDefinitionsByParentId, { datasetId: payload });
        yield put(fetchRiskToleranceDatasetDefinitionsSuccessful(definitions, hiddenFields, riskToleranceDefinitions, documentHiddenFields, riskToleranceIsRecalculating, riskToleranceIncludesOverrides));
    } catch (e) {
        toast.error('Unable to fetch dataset definitions. Please try again.');
        yield put(fetchRiskToleranceDatasetDefinitionsFailed((e as Error).message));
    }
}

function* fetchClientRiskToleranceDatasetDefinitionsWatcher() {
    yield takeLeading(ClientRiskToleranceActionTypes.FETCH_CLIENT_RISK_TOLERANCE_DATASET_DEFINITIONS_STARTED, attemptFetchClientRiskToleranceDatasetDefinitions);
}

export function* checkRiskToleranceUpdated() {
    const currentRiskToleranceConfig: (RiskTolerance | MyRiskTolerance)[] = yield select(getCurrentRiskToleranceConfig);
    const savedRiskToleranceConfig: (RiskTolerance | MyRiskTolerance)[] = yield select(getClientRiskToleranceConfig);
    const hasUpdated = !isEqual(currentRiskToleranceConfig, savedRiskToleranceConfig);
    yield put(setRiskToleranceUpdated(hasUpdated));
}

function* checkRiskToleranceUpdatedWatcher() {
    yield debounce(500, [ClientRiskToleranceActionTypes.UPDATE_RISK_FIELD_CONFIG, ClientRiskToleranceActionTypes.UPDATE_RISK_FIELD_WEIGHTING, ClientRiskToleranceActionTypes.TOGGLE_CLIENT_RISK_FIELD, ClientRiskToleranceActionTypes.UPDATE_RISK_FIELD_FALLBACK_RISK_POSITION], checkRiskToleranceUpdated);
}

export function* attemptSaveClientRiskTolerance() {
    try {
        const currentRiskToleranceConfig: (RiskTolerance | MyRiskTolerance)[] = yield select(getCurrentRiskToleranceConfig);
        const savedRiskToleranceConfig: (RiskTolerance | MyRiskTolerance)[] = yield select(getClientRiskToleranceConfig);
        const strippedRiskToleranceConfig = stripEmptyTextRiskConfig(currentRiskToleranceConfig);
        const updatedRiskToleranceDatasets = strippedRiskToleranceConfig.filter(riskConfig => {
            const savedRiskConfig = savedRiskToleranceConfig.find(({ datasetId }) => riskConfig.datasetId === datasetId);
            return !!savedRiskConfig && !isEqual(savedRiskConfig, riskConfig);
        });
        let newRiskTolerance = savedRiskToleranceConfig;
        let isRecalculating = false;
        if (updatedRiskToleranceDatasets.length > 0) {
            const datasetId: number = yield select(getSelectedDatasetId);
            const riskToleranceIncludesOverrides: boolean = yield select(getRiskToleranceIncludesOverrides);
            const updatedFields: UpdatedRiskField[] = yield select(getRiskFieldsUpdated);
            const response: { newRiskTolerance: (RiskTolerance | MyRiskTolerance)[]; isRecalculating: boolean; } = yield call(upsertClientRiskTolerance, { riskTolerance: updatedRiskToleranceDatasets, datasetId, updatedFields, riskToleranceIncludesOverrides });
            newRiskTolerance = response.newRiskTolerance;
            isRecalculating = response.isRecalculating;
        }
        yield put(saveRiskToleranceSuccessful(newRiskTolerance, isRecalculating));
        toast('Risk Tolerance saved');
    } catch (e) {
        toast.error('Unable to save risk tolerance. Please try again.');
        yield put(saveRiskToleranceFailed());
    }
}

function* saveClientRiskToleranceWatcher() {
    yield takeLeading(ClientRiskToleranceActionTypes.SAVE_CLIENT_RISK_TOLERANCE_STARTED, attemptSaveClientRiskTolerance);
}

export function* attemptCalculateRiskFieldsUpdated({ payload }: ReturnType<typeof toggleRiskFieldsUpdatedModal>) {
    try {
        if (payload) {
            const currentRiskToleranceConfig: (RiskTolerance | MyRiskTolerance)[] = yield select(getCurrentRiskToleranceConfig);
            const savedRiskToleranceConfig: (RiskTolerance | MyRiskTolerance)[] = yield select(getClientRiskToleranceConfig);
            const updatedRiskFields = currentRiskToleranceConfig.reduce((acc: UpdatedRiskField[], riskConfig) => {
                const savedRiskConfig = savedRiskToleranceConfig.find(({ datasetId }) => riskConfig.datasetId === datasetId);
                if (!!savedRiskConfig && !isEqual(savedRiskConfig, riskConfig)) {
                    riskConfig.config.forEach(riskField => {
                        const savedRiskField = savedRiskConfig.config.find(({ sectionId, fieldId }) => isEqual({ fieldId, sectionId }, { fieldId: riskField.fieldId, sectionId: riskField.sectionId }));
                        if (!isUndefined(savedRiskField) && !isEqual(savedRiskField, riskField)) {
                            acc.push({ fieldId: riskField.fieldId, sectionId: riskField.sectionId, datasetId: riskConfig.datasetId, behaviour: null });
                        }
                    });
                }
                return acc;
            }, []);
            yield put(setRiskFieldsUpdated(updatedRiskFields));
        } else {
            yield put(setRiskFieldsUpdated([]));
        }

    } catch (e) {
        toast.error('Unable to establish risk fields updated. Please try again.');
    }
}

function* toggleRiskFieldsUpdatedModalWatcher() {
    yield takeLeading(ClientRiskToleranceActionTypes.TOGGLE_RISK_FIELDS_UPDATED_MODAL, attemptCalculateRiskFieldsUpdated);
}

export function* clientRiskToleranceSaga() {
    yield all([
        fork(fetchAllClientRiskToleranceDatasetsWatcher),
        fork(redirectClientRiskToleranceDatasetViewWatcher),
        fork(fetchClientRiskToleranceDatasetDefinitionsWatcher),
        fork(checkRiskToleranceUpdatedWatcher),
        fork(saveClientRiskToleranceWatcher),
        fork(toggleRiskFieldsUpdatedModalWatcher)
    ]);
}
